import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { styled } from '@mui/material/styles';
import {
    PageToolbar,
    withTemplateCore,
    editionLevels,
} from '@sunwisesoftware/racking-template-core';
import { Box, Container as MuiContainer } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import SwipeableDrawer from 'common/components/SwipeableDrawer';
import { useBreakpoint } from 'common/hooks';
import { LoadingContext } from 'common/utils/contexts';
import { custom1 } from 'common/utils/customScrolls';
import { getCurrentLanguage } from 'common/utils/helpers/multiregion';

import * as actions from './actions';
import {
    HeaderPreview,
    PreviewContent,
    SupportMenuContent,
} from './components';
import { PDF_MEDIUM_QUALITY } from './constants';
import {
    getIsDownloadingFileContextMessage,
    getIsLoadingContextMessage,
    getIsUpdatingContextMessage,
    handleClickDocumentItemBuild,
    handleClickDownloadBuild,
    handleCloseDrawer,
    handleScrollTopSideBar,
} from './helpers';
import * as selectors from './selectors';

const PagesContainer = styled(Box)(
    ({ theme }) => `
        background: ${theme.palette.mode === 'dark' ? '#1b1c1f' : '#f2f4f9'};
        display: flex:
        flex-direction: column;
        overflow: hidden;
        position: relative;
`,
);
const PageWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: scroll;
    ${custom1}
    .apexcharts-theme-light {
        color: black;
    }
`;
const MenuWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    ${custom1}
`;

const Container = ({
    attachments,
    boms,
    datasheets,
    downloadDocumentPdf,
    downloadProposalPdf,
    fetchProposalContent,
    getTemplateUpdatedData,
    initialize,
    initTemplate,
    isDownloading,
    isFetchingProposalContent,
    isFetchingProposalDetails,
    isInitializing,
    isUpdatingCustomTemplate,
    match,
    proposalDetails,
    reset,
    resetTemplate,
    restorePages,
    selectedDocument,
    setSelectedDocument,
    setTemplateBackup,
    systems,
    updateTemplate,
}) => {
    const loadingContext = useContext(LoadingContext);
    const [editionDisabled, setEditionDisabled] = useState(true);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [downloadFormat, setDownloadFormat] = useState(
        parseInt(localStorage.getItem('downloadFormat')) || 2,
    );
    const headerRef = useRef(null);
    const sideBarRef = useRef(null);
    const swipeableDrawerRef = useRef(null);
    const breakpoint = useBreakpoint();
    const proposalId = match.params.uid;

    useEffect(() => {
        getIsLoadingContextMessage({
            isFetchingProposalDetails,
            isFetchingProposalContent,
            isInitializing,
            loadingContext,
        });
    }, [isFetchingProposalDetails, isFetchingProposalContent, isInitializing]);

    useEffect(() => () => reset(), []);

    useEffect(() => {
        if (proposalId) {
            resetTemplate();
            initialize(initTemplate, proposalId, (data) =>
                setSelectedDocument(data),
            );
        }
    }, [proposalId]);

    useEffect(() => {
        getIsDownloadingFileContextMessage(isDownloading, loadingContext);
    }, [isDownloading]);

    useEffect(() => {
        getIsUpdatingContextMessage(
            editionDisabled,
            isUpdatingCustomTemplate,
            loadingContext,
            setEditionDisabled,
        );
    }, [isUpdatingCustomTemplate]);

    useEffect(() => {
        if (!headerRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
            setHeaderHeight(headerRef?.current?.clientHeight);
        });
        resizeObserver.observe(headerRef.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);

    useEffect(() => {
        if (headerRef?.current)
            setHeaderHeight(headerRef?.current?.clientHeight);
    }, [breakpoint]);

    const handleUpdateTemplate = useCallback(() => {
        const { id } = selectedDocument;
        updateTemplate(id, getTemplateUpdatedData, setTemplateBackup);
    }, [selectedDocument, getTemplateUpdatedData, setTemplateBackup]);

    const handleClickDatasheetItem = (datasheet) => {
        resetTemplate();
        setSelectedDocument(datasheet);
        setDownloadFormat(PDF_MEDIUM_QUALITY);
        handleCloseDrawer(swipeableDrawerRef);
    };

    const handleClickDownload = () =>
        handleClickDownloadBuild(
            downloadDocumentPdf,
            downloadProposalPdf,
            downloadFormat,
            loadingContext,
            selectedDocument,
        );

    const handleClickDocumentItem = (document) =>
        handleClickDocumentItemBuild(
            document,
            fetchProposalContent,
            initTemplate,
            resetTemplate,
            selectedDocument,
            setDownloadFormat,
            setSelectedDocument,
            swipeableDrawerRef,
        );

    const handleClickEditButton = () => {
        setEditionDisabled(false);
        setTemplateBackup();
        handleScrollTopSideBar(sideBarRef);
        handleCloseDrawer(swipeableDrawerRef);
    };

    return (
        <>
            <Box display="flex">
                <MuiContainer maxWidth={false} sx={{ p: '0 !important' }}>
                    <HeaderPreview
                        headerRef={headerRef}
                        proposalDetails={proposalDetails}
                    />
                    <PagesContainer
                        sx={{
                            height: {
                                xs: `calc(100vh - ${headerHeight + 52}px)`,
                                md: 'calc(100vh - 92px)',
                            },
                        }}
                    >
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                        >
                            <PageToolbar
                                editionLevel={
                                    editionLevels.PARTIAL_EDITION_MODE
                                }
                                sxToolbar={{
                                    marginLeft: '-8px',
                                }}
                            />
                            <PageWrapper
                                sx={{
                                    height: {
                                        xs: `calc(100vh - ${
                                            headerHeight + 137
                                        }px)`,
                                        md: 'calc(100vh - 141px)',
                                    },
                                    pt: 2,
                                }}
                            >
                                <PreviewContent
                                    selectedDocument={selectedDocument}
                                    editionDisabled={editionDisabled}
                                />
                            </PageWrapper>
                        </Box>
                    </PagesContainer>
                </MuiContainer>
                <MenuWrapper
                    ref={sideBarRef}
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        height: '100vh',
                        p: 2,
                        width: 400,
                    }}
                >
                    <SupportMenuContent
                        attachments={attachments}
                        boms={boms}
                        datasheets={datasheets}
                        downloadFormat={downloadFormat}
                        editionDisabled={editionDisabled}
                        handleClickDatasheetItem={handleClickDatasheetItem}
                        handleClickDocumentItem={handleClickDocumentItem}
                        handleClickDownload={handleClickDownload}
                        handleClickEditButton={handleClickEditButton}
                        handleUpdateTemplate={handleUpdateTemplate}
                        isUpdatingCustomTemplate={isUpdatingCustomTemplate}
                        proposalDetails={proposalDetails}
                        restorePages={restorePages}
                        selectedDocument={selectedDocument}
                        setDownloadFormat={setDownloadFormat}
                        setEditionDisabled={setEditionDisabled}
                        swipeableDrawerRef={swipeableDrawerRef}
                        systems={systems}
                    />
                </MenuWrapper>
            </Box>

            <SwipeableDrawer ref={swipeableDrawerRef}>
                <SupportMenuContent
                    attachments={attachments}
                    boms={boms}
                    datasheets={datasheets}
                    downloadFormat={downloadFormat}
                    editionDisabled={editionDisabled}
                    handleClickDatasheetItem={handleClickDatasheetItem}
                    handleClickDocumentItem={handleClickDocumentItem}
                    handleClickDownload={handleClickDownload}
                    handleClickEditButton={handleClickEditButton}
                    handleUpdateTemplate={handleUpdateTemplate}
                    isUpdatingCustomTemplate={isUpdatingCustomTemplate}
                    proposalDetails={proposalDetails}
                    restorePages={restorePages}
                    selectedDocument={selectedDocument}
                    setDownloadFormat={setDownloadFormat}
                    setEditionDisabled={setEditionDisabled}
                    swipeableDrawerRef={swipeableDrawerRef}
                    systems={systems}
                />
            </SwipeableDrawer>
        </>
    );
};

Container.propTypes = {
    attachments: PropTypes.array,
    boms: PropTypes.array,
    datasheets: PropTypes.array,
    downloadDocumentPdf: PropTypes.func,
    downloadProposalPdf: PropTypes.func,
    fetchProposalContent: PropTypes.func,
    getTemplateUpdatedData: PropTypes.func,
    initialize: PropTypes.func,
    initTemplate: PropTypes.func,
    isDownloading: PropTypes.bool,
    isFetchingProposalContent: PropTypes.bool,
    isFetchingProposalDetails: PropTypes.bool,
    isInitializing: PropTypes.bool,
    isUpdatingCustomTemplate: PropTypes.bool,
    match: PropTypes.object,
    proposalDetails: PropTypes.object,
    reset: PropTypes.func,
    resetTemplate: PropTypes.func,
    restorePages: PropTypes.func,
    selectedDocument: PropTypes.object,
    setSelectedDocument: PropTypes.func,
    setTemplateBackup: PropTypes.func,
    systems: PropTypes.array,
    updateTemplate: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    attachments: selectors.getAttachments,
    boms: selectors.getBoms,
    datasheets: selectors.getDatasheets,
    isDownloading: selectors.getIsDownloading,
    isFetchingProposalContent: selectors.getIsFetchingProposalContent,
    isFetchingProposalDetails: selectors.getIsFetchingProposalDetails,
    isInitializing: selectors.getIsInitializing,
    isUpdatingCustomTemplate: selectors.getIsUpdatingCustomTemplate,
    proposalDetails: selectors.getProposalDetailsData,
    selectedDocument: selectors.getSelectedDocument,
    systems: selectors.getSystems,
});

const mapDispatchToProps = (dispatch) => ({
    downloadDocumentPdf: (archive, loadingContext, name) =>
        dispatch(actions.downloadDocumentPdf(archive, loadingContext, name)),
    downloadProposalPdf: (contentId, downloadFormat, name) =>
        dispatch(actions.downloadProposalPdf(contentId, downloadFormat, name)),
    fetchProposalContent: (contentId, initTemplate) =>
        dispatch(actions.fetchProposalContent(contentId, initTemplate)),
    initialize: (initTemplate, proposalId, callback) =>
        dispatch(actions.initialize(initTemplate, proposalId, callback)),
    reset: () => dispatch(actions.reset()),
    setSelectedDocument: (values) =>
        dispatch(actions.setSelectedDocument(values)),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
    withTemplateCore(() => ({
        baseUrl: import.meta.env.VITE_API_URL_PORTAL,
        editionMode: editionLevels.PARTIAL_EDITION_MODE,
        froalaLicenseKey: import.meta.env.VITE_FROALA_LICENSE,
        googleApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY,
        language: getCurrentLanguage(),
    })),
)(Container);
